<template>
  <TeaserCardsBlock
    :items="teaserCardsProps"
    :grid-type="item.fields.gridType?.toLocaleLowerCase() ?? 'stretch'"
    :header-gutter="false"
    :max-columns="columnWidth"
    :translations="$textDictionary"
    :cols-centered="item.fields.teasersCenteredInGrid ?? true"
    :cols="cols"
    link-icon-position="content"
    class="teaser-cards-block"
    v-bind="
      $ContentfulLivePreview.getProps({
        entryId: item.sys.id,
        fieldId: 'internalName'
      })
    "
  />
</template>

<script setup lang="ts">
import type {
  TeaserCardProps,
  TeaserColumns
} from '@hypercodestudio/basler-components';

import { TeaserCardsBlock } from '@hypercodestudio/basler-components';
import type { IProduct, ITextTeaserCardList } from '~~/lib/ContentfulService';
import { generateImageObject } from '~/utils/generateImageObject';
import { extractContentPageTeaserCardFields } from '~/utils/contentful/extractContentPageTeaserCardFields';
import { isTextTeaserCardLinkVisibleInLocale } from '~/utils/contentful/isTextTeaserCardLinkVisibleInLocale';
import { normalizeTeaserCardText } from '~/utils/contentful/normalizeTeaserCardText';
import { isContentPage } from '~/utils/guards/isContentPage';
import { isDefined } from '~/utils/guards/isDefined';
import { isEntryOfContentType } from '~/utils/guards/isEntryOfContentType';
import { isEntryResolveError } from '~/utils/guards/isEntryResolveError';
import { buildPdpUrl } from '~/utils/buildPdpUrl';
import { buildUrlString } from '~/utils/buildUrlString';
import { mapColsWidth } from '~/utils/mapColsWidth';
import { DecoratedProduct } from '~/utils/shop/DecoratedProduct';
import type {
  CommonProductDetailFragment,
  CommonProductFragment
} from '~~/lib/Shop/generated/schema';
import { teaserCardImageOptions } from '~/utils/imagePresetRegistry';
import {
  hasContentPageTeaserContent,
  hasHeadlineTextImageTeaserContent
} from '~/utils/hasRenderableTeaserContent';
const { $ContentfulLivePreview } = useNuxtApp();

interface Props {
  item: ITextTeaserCardList;
}

const props = defineProps<Props>();

const { $shopStore, $textDictionary, $globalPageSettings, $activeLocale } =
  useNuxtApp();

const cols = computed(() =>
  mapColsWidth(props.item.fields?.gridWith ?? '10 cols')
);
const teaserCardsProps = computed(
  () =>
    props.item.fields?.teasers
      ?.filter((teaser) => !isEntryResolveError(teaser))
      ?.map((currentTeaser): TeaserCardProps | undefined => {
        if (isEntryOfContentType(currentTeaser, 'product')) {
          const product = decoratedProducts.value?.find(
            (product) => product.urlKey === currentTeaser.fields.urlKey
          );

          if (product) {
            return {
              content: product.productDescription ?? '',
              header: product.getProductImage(
                teaserCardImageOptions,
                $globalPageSettings.value?.shopConfig?.defaultProductImage ??
                  undefined
              ),
              headerGutter: false,
              headline: product.modelName ?? '',
              linkIconPosition: 'content',
              link: product.urlKey
                ? useBuildLinkInterface(
                    buildPdpUrl($activeLocale.value.webapp, product.urlKey)
                  )
                : undefined
            } satisfies TeaserCardProps;
          }

          if (decoratedProducts.value != null && !product) {
            // failed to load product, remove from list
            return undefined;
          }

          return {
            isLoading: true,
            content: '',
            header: { type: 'image', content: { src: '' } },
            headerGutter: false,
            headline: '',
            linkIconPosition: 'content',
            link: undefined
          };
        }

        if (isContentPage(currentTeaser)) {
          if (
            currentTeaser.fields?.locales?.length &&
            !currentTeaser.fields.locales.includes(currentTeaser.sys.locale)
          ) {
            return undefined;
          }

          const pageTeaser = extractContentPageTeaserCardFields(currentTeaser);

          if (
            !hasContentPageTeaserContent({
              teaserTitle: pageTeaser.headline,
              teaserText: pageTeaser.content,
              teaserImage: pageTeaser.teaserImage
            })
          ) {
            return undefined;
          }

          return {
            content: pageTeaser.content ?? '',
            header: pageTeaser.teaserImage
              ? generateImageObject(
                  pageTeaser.teaserImage,
                  teaserCardImageOptions
                )
              : undefined,
            headerGutter: false,
            headline: pageTeaser.headline,
            linkIconPosition: 'content',
            link: pageTeaser.slug
              ? useBuildLinkInterface(
                  buildUrlString(currentTeaser.sys.locale, pageTeaser.slug)
                )
              : useBuildLinkInterface(currentTeaser)
          } satisfies TeaserCardProps;
        }

        if (
          !isDefined(currentTeaser) ||
          !isDefined(currentTeaser.fields) ||
          !isTextTeaserCardLinkVisibleInLocale(
            currentTeaser.fields.link?.fields.internalPage,
            currentTeaser.sys?.locale ?? $activeLocale.value.contentfulCode
          )
        ) {
          return undefined;
        }

        if (!hasHeadlineTextImageTeaserContent(currentTeaser.fields)) {
          return undefined;
        }

        return {
          content: normalizeTeaserCardText(currentTeaser.fields?.text),
          header: currentTeaser.fields.image
            ? generateImageObject(
                currentTeaser.fields.image,
                teaserCardImageOptions
              )
            : undefined,
          badge: currentTeaser.fields.badge?.fields.title ?? '',
          headerGutter: false,
          headline: currentTeaser.fields.headline,
          linkIconPosition: 'content',
          link: useBuildLinkInterface(currentTeaser.fields.link)
        } satisfies TeaserCardProps;
      })
      .filter(isDefined) ?? []
);
const teaserCardsProductUrlKeys = computed(
  () =>
    props.item.fields?.teasers
      ?.filter((teaser): teaser is IProduct =>
        isEntryOfContentType(teaser, 'product')
      )
      ?.map((product) => product.fields.urlKey!)
      .filter(isDefined) ?? []
);
const products = (() =>
  teaserCardsProductUrlKeys.value.length
    ? useProducts(
        $shopStore.value,
        $activeLocale.value.webapp,
        computed(() => ({
          url_key: teaserCardsProductUrlKeys.value
        }))
      )
    : undefined)();
const decoratedProducts = computed(() =>
  products?.response.value?.items
    ?.filter(isDefined)
    .map((product: CommonProductFragment | CommonProductDetailFragment) =>
      DecoratedProduct.build(product, {}, $activeLocale.value.webapp)
    )
);
const columnWidth = computed<TeaserColumns>(() => {
  const columns = Number.parseInt(
    props.item.fields.gridColumns?.replace(' columns', '') ?? '3',
    10
  );
  return columns === 1 || columns === 2 || columns === 3 ? columns : 3;
});
</script>
